import { useCallback, useMemo } from 'react'
import { useReactFlow } from '@xyflow/react'
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import {
  setPanelOpen,
  setValidationResult,
  startValidating,
} from '@/features/adp/state/validationSlice'
import { openOverlay, type OverlayType } from '@/features/adp/state/overlaySlice'
import { selectCanvasNodes, selectCanvasEdges } from '@/features/adp/state/canvas/canvasSelectors'
import { getAdpSessionNode } from '@/features/adp/hooks/useAdpSessionStore'
import { validatePipeline, type ValidationIssue } from '@/features/adp/utils/pipelineValidator'
import type { PipelineNode } from '../types/canvas.types'
import styles from './ValidationIssuesPanel.module.scss'

const SEVERITY_LABEL: Record<ValidationIssue['severity'], string> = {
  error: 'Errors',
  warning: 'Warnings',
}

const formatRelative = (timestamp: number | null): string => {
  if (!timestamp) return ''
  const seconds = Math.round((Date.now() - timestamp) / 1000)
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  return `${hours}h ago`
}

export default function ValidationIssuesPanel() {
  const dispatch = useAppDispatch()
  const { setCenter, getNode } = useReactFlow()
  const result = useAppSelector(state => state.validation.result)
  const isPanelOpen = useAppSelector(state => state.validation.isPanelOpen)
  const isDirty = useAppSelector(state => state.validation.isDirty)
  const isValidating = useAppSelector(state => state.validation.isValidating)
  const lastValidatedAt = useAppSelector(state => state.validation.lastValidatedAt)
  const reduxNodes = useAppSelector(selectCanvasNodes)
  const reduxEdges = useAppSelector(selectCanvasEdges)

  const grouped = useMemo(() => {
    if (!result) return null
    const errors: ValidationIssue[] = []
    const warnings: ValidationIssue[] = []
    for (const issue of result.issues) {
      if (issue.severity === 'error') errors.push(issue)
      else warnings.push(issue)
    }
    return { errors, warnings }
  }, [result])

  const handleClose = useCallback(() => {
    dispatch(setPanelOpen(false))
  }, [dispatch])

  const handleOpen = useCallback(() => {
    dispatch(setPanelOpen(true))
  }, [dispatch])

  const handleRevalidate = useCallback(() => {
    dispatch(startValidating())
    const sessionStore: Record<string, unknown> = {}
    for (const node of reduxNodes) {
      const record = getAdpSessionNode(node.id)
      if (record) sessionStore[node.id] = record
    }
    const next = validatePipeline({
      nodes: reduxNodes,
      edges: reduxEdges,
      sessionStore: sessionStore as Record<string, Record<string, unknown>>,
    })
    dispatch(setValidationResult(next))
  }, [dispatch, reduxEdges, reduxNodes])

  const handleLocate = useCallback(
    (issue: ValidationIssue) => {
      const targetNodeId = issue.nodeIds[0]
      if (!targetNodeId) return
      const node = getNode(targetNodeId) as PipelineNode | undefined
      if (!node) return
      const x = node.position.x + (node.measured?.width ?? 240) / 2
      const y = node.position.y + (node.measured?.height ?? 120) / 2
      setCenter(x, y, { zoom: 1.2, duration: 400 })
    },
    [getNode, setCenter]
  )

  const handleFix = useCallback(
    (issue: ValidationIssue) => {
      if (!issue.fix) return
      dispatch(
        openOverlay({
          type: issue.fix.overlayType as OverlayType,
          data: issue.fix.payload as never,
        })
      )
    },
    [dispatch]
  )

  if (!result) return null

  const errorCount = result.summary.errors ?? 0
  const warningCount = result.summary.warnings ?? 0
  const hasWarningsOnly = errorCount === 0 && warningCount > 0

  // Collapsed pill (panel hidden but result exists)
  if (!isPanelOpen) {
    if (result.isValid && !isDirty && warningCount === 0) {
      return (
        <button
          type="button"
          className={`${styles.pill} ${styles.pillSuccess}`}
          onClick={handleOpen}
        >
          <MaterialIcon name="check_circle" size={16} />
          Pipeline validated
        </button>
      )
    }
    if (isDirty) {
      return (
        <button type="button" className={`${styles.pill} ${styles.pillWarning}`} onClick={handleOpen}>
          <MaterialIcon name="info" size={16} />
          Validation outdated · Re-validate
        </button>
      )
    }
    if (hasWarningsOnly) {
      return (
        <button type="button" className={`${styles.pill} ${styles.pillWarning}`} onClick={handleOpen}>
          <MaterialIcon name="warning" size={16} />
          {warningCount} {warningCount === 1 ? 'warning' : 'warnings'}
        </button>
      )
    }
    return (
      <button type="button" className={`${styles.pill} ${styles.pillError}`} onClick={handleOpen}>
        <MaterialIcon name="error" size={16} />
        {errorCount} {errorCount === 1 ? 'error' : 'errors'}
        {warningCount > 0 && (
          <>
            {' · '}
            {warningCount} {warningCount === 1 ? 'warning' : 'warnings'}
          </>
        )}
      </button>
    )
  }

  return (
    <aside className={styles.panel} aria-label="Pipeline validation issues">
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <MaterialIcon
            name={result.isValid ? 'check_circle' : 'error'}
            size={20}
            className={result.isValid ? styles.iconSuccess : styles.iconError}
          />
          <div className={styles.headerText}>
            <div className={styles.headerTitle}>
              {result.isValid ? 'Pipeline is ready' : 'Validation issues'}
            </div>
            <div className={styles.headerSub}>
              {result.summary.errors} {result.summary.errors === 1 ? 'error' : 'errors'}
              {' · '}
              {result.summary.warnings} {result.summary.warnings === 1 ? 'warning' : 'warnings'}
              {' · '}
              {formatRelative(lastValidatedAt)}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Close validation panel"
          onClick={handleClose}
        >
          <MaterialIcon name="close" size={18} />
        </Button>
      </header>

      {isDirty && (
        <div className={styles.dirtyStrip}>
          <MaterialIcon name="info" size={14} />
          Pipeline changed since last validation. Re-validate to refresh.
        </div>
      )}

      <div className={styles.body}>
        {result.issues.length === 0 ? (
          <div className={styles.empty}>
            <MaterialIcon name="task_alt" size={28} className={styles.iconSuccess} />
            <p>All checks passed. You're good to run.</p>
          </div>
        ) : (
          <>
            {grouped && grouped.errors.length > 0 && (
              <IssueGroup
                label={SEVERITY_LABEL.error}
                tone="error"
                issues={grouped.errors}
                onLocate={handleLocate}
                onFix={handleFix}
              />
            )}
            {grouped && grouped.warnings.length > 0 && (
              <IssueGroup
                label={SEVERITY_LABEL.warning}
                tone="warning"
                issues={grouped.warnings}
                onLocate={handleLocate}
                onFix={handleFix}
              />
            )}
          </>
        )}
      </div>

      <footer className={styles.footer}>
        <Button variant="outline" size="sm" onClick={handleRevalidate} disabled={isValidating}>
          <MaterialIcon name="refresh" size={16} />
          {isValidating ? 'Validating…' : 'Re-validate'}
        </Button>
      </footer>
    </aside>
  )
}

interface IssueGroupProps {
  label: string
  tone: 'error' | 'warning'
  issues: ValidationIssue[]
  onLocate: (issue: ValidationIssue) => void
  onFix: (issue: ValidationIssue) => void
}

function IssueGroup({ label, tone, issues, onLocate, onFix }: Readonly<IssueGroupProps>) {
  return (
    <section className={styles.group}>
      <h3 className={`${styles.groupLabel} ${styles[`groupLabel_${tone}`]}`}>
        {label} ({issues.length})
      </h3>
      <ul className={styles.list}>
        {issues.map(issue => (
          <li key={issue.id} className={styles.item}>
            <div className={styles.itemHeader}>
              <MaterialIcon
                name={tone === 'error' ? 'error' : 'warning'}
                size={16}
                className={tone === 'error' ? styles.iconError : styles.iconWarning}
              />
              <div className={styles.itemTitle}>{issue.title}</div>
            </div>
            <div className={styles.itemMessage}>{issue.message}</div>
            <div className={styles.itemActions}>
              {issue.nodeIds.length > 0 && (
                <button
                  type="button"
                  className={styles.actionGhost}
                  onClick={() => onLocate(issue)}
                >
                  <MaterialIcon name="my_location" size={14} />
                  Locate
                </button>
              )}
              {issue.fix && (
                <button type="button" className={styles.actionPrimary} onClick={() => onFix(issue)}>
                  <MaterialIcon name="build" size={14} />
                  Fix
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
